import { useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { FEEDBACK_MODES } from "@/lib/checkins";
import { errMsg } from "../staff/ResolveDialog";
import { ConsentFields } from "./EnrolDialog";
import { DueDatePicker, todayMelbourne } from "./DueDatePicker";

const TITLES = { pause: "Pause progress reviews", resume: "Resume progress reviews", end: "End enrolment", cadence: "Change cadence",
  feedback_mode: "Change feedback release", practitioner: "Change practitioner", reenrol: "Re-enrol with fresh consent" };
const DONE = { pause: "Progress reviews paused", resume: "Progress reviews resumed", end: "Enrolment ended", cadence: "Cadence updated",
  feedback_mode: "Feedback release updated", practitioner: "Practitioner updated", reenrol: "Client re-enrolled" };
const NOTES = { end: "Stops future invitations and revokes any open link. History is kept.",
  pause: "No invitations or reminders are sent while paused.",
  reenrol: "Re-enrolment always needs the client's new, explicit agreement — it is never automatic." };

export function EnrolmentActionDialog({ enrolment, action, staff, wording, onClose, onDone }) {
  const [reason, setReason] = useState("");
  const [cadence, setCadence] = useState(String(enrolment.cadence_days === 28 ? 42 : 28));
  const [reset, setReset] = useState(false);
  const [mode, setMode] = useState(enrolment.feedback_mode);
  const [prac, setPrac] = useState(enrolment.practitioner_id || "");
  const [consent, setConsent] = useState({ given: false, method: "verbal" });
  const [start, setStart] = useState(todayMelbourne());
  const [busy, setBusy] = useState(false);
  async function save() {
    setBusy(true);
    const body = { action, reason: reason || null, cadence_days: Number(cadence), reset_from_completion: reset,
      feedback_mode: mode, practitioner_id: prac, consent: action === "reenrol" ? consent : null,
      start_date: action === "reenrol" ? start : null };
    try {
      await api.patch(`/checkin-enrolments/${enrolment.id}`, body);
      toast.success(DONE[action]);
      onDone();
      onClose();
    } catch (e) {
      toast.error("Couldn't update", { description: errMsg(e) });
    } finally {
      setBusy(false);
    }
  }
  const needsReason = action === "cadence";
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid={`checkin-action-dialog-${action}`}>
        <DialogHeader><DialogTitle>{TITLES[action]}</DialogTitle>{NOTES[action] && <DialogDescription>{NOTES[action]}</DialogDescription>}</DialogHeader>
        <div className="space-y-3">
          {action === "cadence" && (
            <>
              <Select value={cadence} onValueChange={setCadence}>
                <SelectTrigger data-testid="checkin-action-cadence"><SelectValue /></SelectTrigger>
                <SelectContent>{(enrolment.allowed_cadence || [28, 42]).map((d) => <SelectItem key={d} value={String(d)}>Every {d / 7} weeks</SelectItem>)}</SelectContent>
              </Select>
              <label className="flex items-center gap-2 text-sm"><Checkbox checked={reset} onCheckedChange={(v) => setReset(!!v)} data-testid="checkin-action-reset" />Count the next due date from the last completion</label>
            </>
          )}
          {action === "feedback_mode" && (
            <Select value={mode} onValueChange={setMode}>
              <SelectTrigger data-testid="checkin-action-feedback-mode"><SelectValue /></SelectTrigger>
              <SelectContent>{Object.entries(FEEDBACK_MODES).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
            </Select>
          )}
          {action === "practitioner" && (
            <Select value={prac} onValueChange={setPrac}>
              <SelectTrigger data-testid="checkin-action-practitioner"><SelectValue /></SelectTrigger>
              <SelectContent>{staff.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}</SelectContent>
            </Select>
          )}
          {action === "reenrol" && (
            <>
              <div><Label>First due date</Label><DueDatePicker value={start} onChange={setStart} testId="checkin-action-start" /></div>
              <ConsentFields wording={wording} consent={consent} setConsent={setConsent} />
            </>
          )}
          {action !== "reenrol" && (
            <div><Label>Reason{needsReason ? "" : " (optional)"}</Label><Textarea rows={2} value={reason} onChange={(e) => setReason(e.target.value)} data-testid="checkin-action-reason" /></div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} data-testid="checkin-action-save"
            disabled={busy || (needsReason && !reason.trim()) || (action === "reenrol" && !consent.given)}>Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
